import { createFileRoute } from "@tanstack/react-router";
import { Mail, Phone, Clock } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ContactForm } from "@/components/storefront/ContactForm";
import { Card, CardContent } from "@/components/ui/card";

const hours: { day: string; time: string; closed?: boolean }[] = [
  { day: "Δευτέρα", time: "10:00 – 16:00 & 18:00 – 20:00" },
  { day: "Τρίτη", time: "10:00 – 16:00" },
  { day: "Τετάρτη", time: "10:00 – 16:00 & 18:00 – 20:00" },
  { day: "Πέμπτη", time: "10:00 – 16:00" },
  { day: "Παρασκευή", time: "10:00 – 16:00 & 18:00 – 20:00" },
  { day: "Σάββατο", time: "Κλειστά", closed: true },
  { day: "Κυριακή", time: "Κλειστά", closed: true },
];

const contactCards = [
  {
    icon: Mail,
    emoji: "✉️",
    label: "Email",
    value: "info@mrpanda.gr",
    href: "mailto:info@mrpanda.gr",
  },
  {
    icon: Phone,
    emoji: "📞",
    label: "Τηλέφωνο",
    value: "2311 822 500",
    href: "tel:+302311822500",
  },
] as const;

function ContactPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Παιχνιδιάρικο hero */}
        <div className="mp-playful-panel relative overflow-hidden rounded-3xl border p-7 sm:p-10">
          <span className="pointer-events-none absolute right-6 top-6 animate-float-y text-2xl opacity-70" aria-hidden>
            ✨
          </span>
          <span className="pointer-events-none absolute bottom-5 right-14 animate-bob text-xl opacity-60" aria-hidden>
            💬
          </span>
          <div className="flex items-center gap-3">
            <span className="hover-wiggle animate-bob inline-block text-4xl sm:text-5xl" aria-hidden>
              🐼
            </span>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Επικοινωνία</h1>
          </div>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Είμαστε εδώ για σένα! 🙌 Στείλε μας μήνυμα ή χρησιμοποίησε τα παρακάτω στοιχεία — το panda team
            απαντάει πάντα με χαμόγελο.
          </p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:gap-10">
          {/* Στοιχεία επικοινωνίας */}
          <div className="space-y-4">
            {contactCards.map((c, i) => (
              <Card
                key={c.label}
                className="mp-bouncy animate-pop-in border-primary/10"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <CardContent className="flex items-center gap-4 p-5">
                  <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground shadow-md">
                    <c.icon className="h-5 w-5" />
                    <span className="absolute -right-1.5 -top-1.5 text-base" aria-hidden>
                      {c.emoji}
                    </span>
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-muted-foreground">{c.label}</h2>
                    <a href={c.href} className="text-lg font-semibold transition-colors hover:text-primary">
                      {c.value}
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card
              className="mp-bouncy animate-pop-in border-primary/10"
              style={{ animationDelay: `${contactCards.length * 90}ms` }}
            >
              <CardContent className="flex items-start gap-4 p-5">
                <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground shadow-md">
                  <Clock className="h-5 w-5" />
                  <span className="absolute -right-1.5 -top-1.5 text-base" aria-hidden>
                    ⏰
                  </span>
                </span>
                <div className="w-full">
                  <h2 className="text-sm font-semibold text-muted-foreground">Ωράριο λειτουργίας</h2>
                  <dl className="mt-3 space-y-2">
                    {hours.map((h) => (
                      <div
                        key={h.day}
                        className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                      >
                        <dt className="font-medium">{h.day}</dt>
                        <dd className="sm:text-right">
                          {h.closed ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                              😴 Κλειστά
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">{h.time}</span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Φόρμα επικοινωνίας */}
          <Card className="animate-pop-in border-primary/10" style={{ animationDelay: "120ms" }}>
            <CardContent className="p-5 sm:p-6">
              <h2 className="mb-1 text-xl font-semibold">Πες μας τα νέα σου 👋</h2>
              <p className="mb-4 text-sm text-muted-foreground">Συμπλήρωσε τη φόρμα και θα σου απαντήσουμε άμεσα.</p>
              <ContactForm />
            </CardContent>
          </Card>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/contact" },
      { title: "Επικοινωνία — MrPanda.gr" },
      { name: "description", content: "Επικοινώνησε με την ομάδα του MrPanda.gr μέσω email, τηλεφώνου ή της φόρμας επικοινωνίας." },
      { property: "og:title", content: "Επικοινωνία — MrPanda.gr" },
      { property: "og:description", content: "Επικοινώνησε με την ομάδα του MrPanda.gr μέσω email, τηλεφώνου ή της φόρμας επικοινωνίας." },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/contact" }],
  }),
  component: ContactPage,
});
